/**
 * 「X のカードのタグ」の欄（X の共有の画面・pages/SharePage.tsx の下）。2026-09-30 console/share-tags。
 *
 * WHY この欄か（founder の決定・案 D 2026-09-30）: X のカードのタグを Kumo がアプリの </head> に差し込むのをやめ（Core が閉じた）、
 *   作者が自分のソースの <head> に貼る形にした。だから画面は ① 貼るタグ（応答の html）をコードの塊で見せてコピー、
 *   ② 何が起きるかの説明、③ AI エージェントに頼む文のコピー、の 3 つ。宣伝の言葉は置かない（MASTER-PLAN §1c）。
 * サーバー: GET …/environments/:env/share/tags（読むだけ・何度読んでも何も変えない）。応答の読み方は shareTags.ts。
 * 環境: 画面を開いている環境（development / production）の値をそのまま出す。production のタグは production の画像を指す（応答どおり）。
 * 画像が無い（404 share_images_disabled = shareImages が OFF）: 「ON にすると出ます」と、ON にするコマンド（FeatureHint と同じ形）・
 *   概要の「機能」への道を出す。ほかのエラーは普通のエラー表示（ErrorState）。
 * 共有ごとのカード（?ks=）は出さない: 静的なサイトでは共有 ID ごとに <head> を変えられない = 既定のカードのタグだけ。
 */
import { Link } from 'react-router-dom';
import { CLI_NAME } from '../cliCommand';
import { enableCommands } from '../featureToggle';
import { useResource } from '../hooks';
import { useT } from '../i18n';
import { isShareImagesOff, shareTagsCommand, shareTagsView, type ShareTagsResponse } from '../shareTags';
import { CodeBlock, ErrorState, Spinner } from './ui';

export function ShareTagsCard({ base, env }: { base: string; env: 'development' | 'production' }) {
  const r = useResource<ShareTagsResponse>(`${base}/share/tags`);
  const { t, tr } = useT();
  const envName = t(`env.inline.${env}`);
  const view = shareTagsView(r.data);
  return (
    <section className="card share-tags" aria-labelledby="share-tags-title">
      <h2 className="card-title" id="share-tags-title">
        {t('share.tags.title')}
      </h2>
      <p className="small muted">{t('share.tags.lead')}</p>
      {r.loading && !r.data && <Spinner />}
      {r.error &&
        (isShareImagesOff(r.error) ? (
          <div className="alert alert-info" role="note">
            <strong>{t('share.tags.imagesOff', { feature: t('feature.shareImages') })}</strong>
            <span>{tr('share.tags.imagesOffWhere', { link: <Link to="../overview">{t('featureHint.link')}</Link> })}</span>
            {/* 手元で ON にして反映するコマンド（FeatureHint と同じ形・--env つき = 本番の画面を見ながら開発環境に push しない） */}
            <CodeBlock code={enableCommands(['shareImages'], env)} />
          </div>
        ) : (
          <ErrorState error={r.error} what={t('share.tags.title')} onRetry={r.reload} />
        ))}
      {r.data && (
        <>
          <p className="small muted">{t('share.tags.env', { env: envName })}</p>
          <CodeBlock code={view.html} label={t('share.tags.label')} />
          {view.image && (
            <figure className="share-card-preview">
              {/* 画像の口は鍵なし（X のクローラーが読む口と同じ）なので、そのまま <img> で見せられる */}
              <img src={view.image} alt={t('share.tags.image')} loading="lazy" />
              <figcaption className="small muted">
                {t('share.tags.image')} · <span className="mono">{view.image}</span>
              </figcaption>
            </figure>
          )}
          <p className="small muted">{t('share.tags.askLead')}</p>
          {/* コマンド名は CLI_NAME の 1 か所から（直書きしない・console/kumodeck が名前を変える） */}
          <CodeBlock code={t('share.tags.ask', { cmd: shareTagsCommand(CLI_NAME) })} label={t('share.tags.askLabel')} />
        </>
      )}
    </section>
  );
}
